import React, { useState, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useQuery } from 'react-query';
import { Tabs, Tab, Table, Alert } from 'react-bootstrap';
import { INVENTORY_SESSION_API } from '@app/utils/apiUrl';
import { Get } from '@app/utils/httpProvider';
import { fetchWithAuth } from '@app/utils/httpClient';
import { toast } from 'react-toastify';
import { URI_INTERNAL } from '../../utils/pathLocations';
import { formatDateTimeMoment } from '../../utils/helper';
import Pagination from 'react-js-pagination';

// Hàm tính màu text tương phản dựa trên màu hex nền
const hexToLightBg = (hex) => {
    if (!hex) return { bg: '#f0f0f0', border: '#ccc', text: '#555' };
    const clean = hex.replace('#', '');
    const r = parseInt(clean.substring(0, 2), 16);
    const g = parseInt(clean.substring(2, 4), 16);
    const b = parseInt(clean.substring(4, 6), 16);
    return {
        bg: `rgba(${r},${g},${b},0.12)`,
        border: `rgba(${r},${g},${b},0.5)`,
        text: hex
    };
};

const AssetStatusBadge = ({ statusName, statusColor }) => {
    let bg, border, text;
    const nameLower = String(statusName || '').toLowerCase();

    if (nameLower.includes('đào thải')) {
        bg = '#212529'; // Đen
        text = '#ffffff';
        border = '#212529';
    } else if (nameLower.includes('hỏng') || nameLower.includes('sửa')) {
        bg = '#dc3545'; // Đỏ
        text = '#ffffff';
        border = '#dc3545';
    } else if (nameLower.includes('chờ phê duyệt')) {
        bg = '#ffc107'; // Vàng
        text = '#000000';
        border = '#ffc107';
    } else if (nameLower === 'tài sản mới' || nameLower === 'mới') {
        bg = '#007bff'; // Xanh dương
        text = '#ffffff';
        border = '#007bff';
    } else if (nameLower.includes('điều chuyển')) {
        bg = '#17a2b8'; // Xanh ngọc (Teal)
        text = '#ffffff';
        border = '#17a2b8';
    } else if (nameLower.includes('đang sử dụng')) {
        bg = '#28a745'; // Xanh lá
        text = '#ffffff';
        border = '#28a745';
    } else {
        const generated = hexToLightBg(statusColor);
        bg = generated.bg;
        border = generated.border;
        text = generated.text;
    }

    return (
        <span
            style={{
                display: 'inline-block',
                padding: '3px 11px',
                borderRadius: '999px',
                fontSize: '11px',
                fontWeight: 700,
                background: bg,
                color: text || '#555',
                border: `1px solid ${border}`,
                whiteSpace: 'nowrap',
                letterSpacing: '0.3px'
            }}
        >
            {statusName}
        </span>
    );
};

const InventorySessionDetail = () => {
    const { id } = useParams();
    const history = useHistory();

    const [expandedRows, setExpandedRows] = useState(new Set());
    const [isExporting, setIsExporting] = useState(false);

    // Paging states for local pagination
    const [page1, setPage1] = useState(1);
    const pageSize = 10;

    // 1. Lấy thông tin phiếu
    const { isLoading: isLoadingSession, data: sessionData } = useQuery(
        ['inventorySessionDetail', id],
        () => Get(`${INVENTORY_SESSION_API.GET_ITEM}/${id}`)
    );

    // 2. Lấy danh sách snapshot (lấy tối đa để filter local)
    const { isLoading: isLoadingSnapshots, data: snapshotData } = useQuery(
        ['inventorySessionSnapshots', id],
        () => Get(`${INVENTORY_SESSION_API.GET_SNAPSHOTS}/${id}`, null, { PageIndex: 1, PageSize: 10000 })
    );

    const allSnapshots = snapshotData?.data || [];

    // Local pagination slices
    const currentSnapshots = allSnapshots.slice((page1 - 1) * pageSize, page1 * pageSize);

    const toggleExpandRow = (rowId) => {
        setExpandedRows(prev => {
            const next = new Set(prev);
            if (next.has(rowId)) next.delete(rowId);
            else next.add(rowId);
            return next;
        });
    };

    const goBack = () => {
        history.push(URI_INTERNAL.INVENTORY_SESSION_PATH);
    };

    const handleExportExcel = async () => {
        setIsExporting(true);
        try {
            const response = await fetchWithAuth(INVENTORY_SESSION_API.EXPORT_EXCEL(id), {
                method: 'GET'
            });

            if (response.ok) {
                const blob = await response.blob();
                const fileName = `BienBanKiemKe_${sessionData?.sessionCode || id}.xlsx`;
                
                const url = window.URL.createObjectURL(new Blob([blob]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', fileName);
                document.body.appendChild(link);
                link.click();
                
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
                toast.success('Tải biên bản kiểm kê thành công!');
            } else {
                toast.error('Có lỗi xảy ra khi tải file.');
            }
        } catch (error) {
            console.error('Lỗi export excel:', error);
            toast.error('Lỗi kết nối máy chủ.');
        } finally {
            setIsExporting(false);
        }
    };

    if (isLoadingSession) return <div className="p-4 text-center">Đang tải thông tin...</div>;
    if (!sessionData) return <div className="p-4 text-center text-danger">Không tìm thấy thông tin phiếu kiểm kê.</div>;

    return (
        <div className="container-fluid">
            <div className="row">
                <div className="col-lg-12">
                    {sessionData.isCancelled && (
                        <Alert variant="danger" className="border-danger shadow-sm mb-3">
                            <i className="fas fa-exclamation-triangle mr-2"></i>
                            Phiếu kiểm kê này đã bị hủy bởi <strong>{sessionData.cancelledBy || 'Hệ thống'}</strong> vào lúc <strong>{formatDateTimeMoment(sessionData.cancelledDate)}</strong>.
                            <br />
                            <small className="text-muted">Trạng thái các tài sản đào thải trong phiếu đã được khôi phục về trạng thái trước đó.</small>
                        </Alert>
                    )}
                    <div className="card mb-3">
                        <div className="card-header bg-white py-3 d-flex justify-content-between align-items-center">
                            <h5 className="card-title fw-bold text-primary mb-0">
                                <i className="fas fa-info-circle mr-2" />
                                Chi tiết Phiếu kiểm kê: {sessionData.sessionCode}
                            </h5>
                            <div className="ml-auto d-flex">
                                <button 
                                    type="button" 
                                    className="btn btn-success btn-sm mr-2" 
                                    disabled={isExporting}
                                    onClick={handleExportExcel}
                                >
                                    <i className={isExporting ? "fas fa-spinner fa-spin mr-1" : "fas fa-file-excel mr-1"}></i> 
                                    {isExporting ? 'Đang xuất...' : 'Xuất Excel'}
                                </button>
                                <button type="button" className="btn btn-secondary btn-sm" onClick={goBack}>
                                    <i className="fas fa-arrow-left mr-1"></i> Quay lại
                                </button>
                            </div>
                        </div>
                        <div className="card-body">
                            <div className="row">
                                <div className="col-md-6 mb-2">
                                    <strong>Tiêu đề:</strong> <span className="ml-2 text-dark">{sessionData.title}</span>
                                </div>
                                <div className="col-md-6 mb-2">
                                    <strong>Trạng thái:</strong> 
                                    {sessionData.isCancelled ? (
                                        <span className="badge badge-danger px-2 py-1 ml-2" style={{ borderRadius: '4px' }}>Đã hủy</span>
                                    ) : (
                                        <span className="badge badge-success px-2 py-1 ml-2" style={{ borderRadius: '4px' }}>Đã chốt</span>
                                    )}
                                </div>
                                <div className="col-md-6 mb-2">
                                    <strong>Người tạo:</strong> <span className="ml-2 text-dark">{sessionData.createdBy || 'Hệ thống'}</span>
                                </div>
                                <div className="col-md-6 mb-2">
                                    <strong>Ngày chốt:</strong> <span className="ml-2 text-dark">{formatDateTimeMoment(sessionData.snapshotDate)}</span>
                                </div>
                                <div className="col-md-6 mb-2">
                                    <strong>Tổng số tài sản chốt:</strong> <span className="ml-2 font-weight-bold text-primary">{sessionData.totalAssets}</span>
                                </div>
                                {/* <div className="col-md-6 mb-2">
                                    <strong>Số tài sản đào thải đợt này:</strong> <span className="ml-2 font-weight-bold text-danger">{sessionData.totalDisposals}</span>
                                </div> */}
                                <div className="col-md-12 mt-2">
                                    <strong>Ghi chú:</strong>
                                    <p className="mt-1 mb-0 p-2 bg-light border rounded text-muted">
                                        {sessionData.notes || <i>Không có ghi chú</i>}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="card">
                        <div className="card-body p-0">
                                    <div className="p-3">
                                        {isLoadingSnapshots ? (
                                            <div className="text-center py-3">Đang tải danh sách tài sản...</div>
                                        ) : (
                                            <>
                                                <div className="table-responsive">
                                                    <Table striped bordered hover size="sm" className="mobile-card-view">
                                                        <thead className="bg-light">
                                                            <tr>
                                                                <th style={{width: '40px'}}></th>
                                                                <th className="text-center">STT</th>
                                                                <th>Mã TS</th>
                                                                <th>Tên TS</th>
                                                                <th>Phòng ban</th>
                                                                <th>Nhóm TS</th>
                                                                <th>Tình trạng</th>
                                                                <th>Đào thải lần này</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {currentSnapshots.length > 0 ? currentSnapshots.map((item, idx) => (
                                                                <React.Fragment key={item.id}>
                                                                    <tr>
                                                                        <td className="text-center align-middle cursor-pointer" data-label="Chi tiết" onClick={() => toggleExpandRow(item.id)}>
                                                                            <i className={`fas fa-chevron-${expandedRows.has(item.id) ? 'down' : 'right'} text-primary`}></i>
                                                                        </td>
                                                                        <td className="text-center align-middle" data-label="STT">{(page1 - 1) * pageSize + idx + 1}</td>
                                                                        <td className="align-middle fw-bold" data-label="Mã TS">{item.assetCode}</td>
                                                                        <td className="align-middle" data-label="Tên TS">{item.assetName}</td>
                                                                        <td className="align-middle" data-label="Phòng ban">{item.departmentName}</td>
                                                                        <td className="align-middle" data-label="Nhóm TS">{item.assetGroupName}</td>
                                                                        <td className="align-middle" data-label="Tình trạng">
                                                                            <AssetStatusBadge statusName={item.statusName} statusColor={item.statusColor} />
                                                                        </td>
                                                                        <td className="text-center align-middle" data-label="Đào thải">
                                                                            {item.isDisposed ? <span className="badge badge-danger">Có</span> : <span className="badge badge-light border">Không</span>}
                                                                        </td>
                                                                    </tr>
                                                                    {expandedRows.has(item.id) && (
                                                                        <tr className="expand-row">
                                                                            <td colSpan="8" className="bg-light p-3">
                                                                                {item.accessories && item.accessories.length > 0 ? (
                                                                                    <div className="bg-white p-2 border rounded">
                                                                                        <h6 className="font-weight-bold text-secondary mb-2" style={{ fontSize: '13px' }}><i className="fas fa-tools mr-1"></i> Phụ kiện đính kèm</h6>
                                                                                        <Table size="sm" bordered className="mb-0">
                                                                                            <thead className="bg-light">
                                                                                                <tr>
                                                                                                    <th>Tên vật tư/phụ kiện</th>
                                                                                                    <th>Mã vật tư</th>
                                                                                                    <th className="text-center">Số lượng</th>
                                                                                                    <th>Tình trạng</th>
                                                                                                </tr>
                                                                                            </thead>
                                                                                            <tbody>
                                                                                                {item.accessories.map((acc, aIdx) => (
                                                                                                    <tr key={aIdx}>
                                                                                                        <td>{acc.materialName}</td>
                                                                                                        <td>{acc.materialCode}</td>
                                                                                                        <td className="text-center">{acc.quantity}</td>
                                                                                                        <td>{acc.accessoryStatus}</td>
                                                                                                    </tr>
                                                                                                ))}
                                                                                            </tbody>
                                                                                        </Table>
                                                                                    </div>
                                                                                ) : (
                                                                                    <div className="text-muted text-center italic" style={{ fontSize: '13px' }}>Không có phụ kiện đính kèm</div>
                                                                                )}
                                                                            </td>
                                                                        </tr>
                                                                    )}
                                                                </React.Fragment>
                                                            )) : (
                                                                <tr><td colSpan="8" className="text-center">Không có dữ liệu snapshot</td></tr>
                                                            )}
                                                        </tbody>
                                                    </Table>
                                                </div>
                                                {allSnapshots.length > pageSize && (
                                                    <div className="d-flex justify-content-center mt-3">
                                                        <Pagination
                                                            itemClass="page-item" linkClass="page-link" innerClass="pagination pagination-sm mb-0"
                                                            activePage={page1} itemsCountPerPage={pageSize} totalItemsCount={allSnapshots.length} pageRangeDisplayed={5}
                                                            onChange={setPage1}
                                                        />
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </div>
                        </div>
                    </div>
                </div>
            </div>
            
            <style>{`
                .custom-tabs .nav-link { color: #495057; font-weight: 500; padding: 10px 20px; border: none; border-bottom: 2px solid transparent; }
                .custom-tabs .nav-link:hover { border-color: transparent; background: #f8f9fa; }
                .custom-tabs .nav-link.active { color: #0d6efd; background-color: transparent; border-color: transparent; border-bottom: 2px solid #0d6efd; }
                .cursor-pointer { cursor: pointer; }

                @media (max-width: 767.98px) {
                    .mobile-card-view tr.expand-row {
                        border-bottom: 3px solid #e0e0e0 !important;
                    }
                    .mobile-card-view tr.expand-row td {
                        display: block !important;
                        text-align: left !important;
                        padding: 15px !important;
                    }
                    .mobile-card-view tr.expand-row td::before {
                        display: none !important;
                    }
                    /* Reset lồng nhau cho bảng phụ kiện bên trong */
                    .mobile-card-view table td {
                        display: table-cell !important;
                        text-align: left !important;
                        width: auto !important;
                        border: 1px solid #dee2e6 !important;
                        padding: .3rem !important;
                    }
                    .mobile-card-view table td::before {
                        display: none !important;
                    }
                    .mobile-card-view table thead {
                        display: table-header-group !important;
                    }
                    .mobile-card-view table tr {
                        display: table-row !important;
                        border-bottom: none !important;
                        padding: 0 !important;
                    }
                }
            `}</style>
        </div>
    );
};

export default InventorySessionDetail;
